/**
 * PTOManagement.js - Admin PTO Request Management
 * 
 * Allows admins to:
 * - View all PTO requests
 * - Filter by status (Pending, Approved, Denied)
 * - Approve or deny pending requests
 * - See which dates are affected
 * 
 * When PTO is approved, the schedule auto-updates to remove the user.
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import dataStore from '../data/dataStore';

function PTOManagement() {
  const { theme, colors, darkMode } = useTheme();
  const { user: currentUser } = useAuth();
  
  const [ptoRequests, setPtoRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('All'); // All, Pending, Approved, Denied
  const [message, setMessage] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  
  // Admin PTO submission state
  const [showAdminPTOForm, setShowAdminPTOForm] = useState(false);
  const [adminSelectedDates, setAdminSelectedDates] = useState([]);
  const [adminReason, setAdminReason] = useState('');
  const [adminIsPartialDay, setAdminIsPartialDay] = useState(false);
  const [adminPartialStartTime, setAdminPartialStartTime] = useState('08:00');
  const [adminPartialHours, setAdminPartialHours] = useState(4);

  // Load data
  const loadData = () => {
    setPtoRequests(dataStore.getPTORequests());
    setUsers(dataStore.getUsers());
  };

  useEffect(() => {
    loadData();
  }, []);

  // Clear message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown User';
  };

  // Filter requests
  const filteredRequests = ptoRequests.filter(req => {
    if (filter === 'All') return true;
    return req.status === filter;
  });

  // Sort by date (newest first)
  const sortedRequests = [...filteredRequests].sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  );

  // Approve request
  const handleApprove = async (requestId) => {
    try {
      await dataStore.approvePTORequest(requestId, currentUser);
      loadData();
      setMessage('✅ PTO request approved! Schedule has been updated.');
    } catch (err) {
      console.error('Error approving PTO:', err);
      setMessage('Error: ' + err.message);
    }
  };

  // Deny request
  const handleDeny = async (requestId) => {
    if (window.confirm('Are you sure you want to deny this PTO request?')) {
      try {
        await dataStore.denyPTORequest(requestId, currentUser);
        loadData();
        setMessage('PTO request denied.');
      } catch (err) {
        console.error('Error denying PTO:', err);
        setMessage('Error: ' + err.message);
      }
    }
  };

  // Delete approved PTO request
  const handleDelete = async (requestId) => {
    const request = ptoRequests.find(r => r.id === requestId);
    const userName = getUserName(request?.userId);
    if (window.confirm(`Are you sure you want to delete this approved PTO for ${userName}?\n\nThis will remove the PTO from the schedule.`)) {
      try {
        await dataStore.cancelPTORequest(requestId, currentUser);
        loadData();
        setMessage('✅ Approved PTO deleted successfully.');
      } catch (err) {
        console.error('Error deleting PTO:', err);
        setMessage('Error: ' + err.message);
      }
    }
  };

  // Format date for display - MM/DD/YYYY format
  const formatDateShort = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  // Format an array of dates, grouping consecutive dates into ranges
  const formatDateRanges = (dates) => {
    if (!dates || dates.length === 0) return '-';
    
    // Sort dates first
    const sortedDates = [...dates].sort();
    
    // Group consecutive dates into ranges
    const ranges = [];
    let rangeStart = sortedDates[0];
    let rangeEnd = sortedDates[0];
    
    for (let i = 1; i < sortedDates.length; i++) {
      const currentDate = new Date(sortedDates[i] + 'T12:00:00');
      const prevDate = new Date(sortedDates[i - 1] + 'T12:00:00');
      
      // Check if current date is exactly 1 day after previous
      const diffDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
      
      if (diffDays === 1) {
        // Extend the current range
        rangeEnd = sortedDates[i];
      } else {
        // Save the previous range and start a new one
        ranges.push({ start: rangeStart, end: rangeEnd });
        rangeStart = sortedDates[i];
        rangeEnd = sortedDates[i];
      }
    }
    
    // Don't forget the last range
    ranges.push({ start: rangeStart, end: rangeEnd });
    
    // Format ranges as strings
    return ranges.map(range => {
      if (range.start === range.end) {
        return formatDateShort(range.start);
      } else {
        return `${formatDateShort(range.start)} - ${formatDateShort(range.end)}`;
      }
    }).join(', ');
  };

  // Group approved PTOs by teacher with merged consecutive dates (filtered by selected calendar month)
  const getApprovedPTOGrouped = () => {
    const selectedYear = calendarMonth.getFullYear();
    const selectedMonth = calendarMonth.getMonth();
    
    const approvedRequests = ptoRequests.filter(p => p.status === 'Approved');
    
    // Group by teacher
    const byTeacher = {};
    
    approvedRequests.forEach(request => {
      const teacherName = getUserName(request.userId);
      
      request.dates.forEach(dateStr => {
        const dateObj = new Date(dateStr + 'T12:00:00');
        // Only include dates within the selected calendar month
        if (dateObj.getFullYear() === selectedYear && dateObj.getMonth() === selectedMonth) {
          if (!byTeacher[teacherName]) {
            byTeacher[teacherName] = [];
          }
          byTeacher[teacherName].push({
            date: dateStr,
            isPartialDay: request.isPartialDay,
            partialStartTime: request.partialStartTime,
            partialHours: request.partialHours
          });
        }
      });
    });
    
    // For each teacher, merge consecutive dates into ranges
    const result = [];
    
    Object.entries(byTeacher).forEach(([teacherName, entries]) => {
      // Sort entries by date
      entries.sort((a, b) => a.date.localeCompare(b.date));
      
      // Group consecutive dates (only merge if same partial day status)
      let currentRange = null;
      
      entries.forEach(entry => {
        if (!currentRange) {
          currentRange = { 
            startDate: entry.date, 
            endDate: entry.date, 
            teacherName,
            isPartialDay: entry.isPartialDay,
            partialStartTime: entry.partialStartTime,
            partialHours: entry.partialHours
          };
        } else {
          // Check if this date is consecutive to the current range end
          const prevDate = new Date(currentRange.endDate + 'T12:00:00');
          const currDate = new Date(entry.date + 'T12:00:00');
          const diffDays = (currDate - prevDate) / (1000 * 60 * 60 * 24);
          
          // Only merge if consecutive AND same partial day status
          if (diffDays === 1 && entry.isPartialDay === currentRange.isPartialDay) {
            currentRange.endDate = entry.date;
          } else {
            // Save current range and start new one
            result.push(currentRange);
            currentRange = { 
              startDate: entry.date, 
              endDate: entry.date, 
              teacherName,
              isPartialDay: entry.isPartialDay,
              partialStartTime: entry.partialStartTime,
              partialHours: entry.partialHours
            };
          }
        }
      });
      
      // Don't forget the last range
      if (currentRange) {
        result.push(currentRange);
      }
    });
    
    // Sort by start date
    result.sort((a, b) => a.startDate.localeCompare(b.startDate));
    
    return result;
  };

  // Calendar navigation
  const prevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  // Get calendar data for current month (for admin PTO form)
  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startingDay = firstDay.getDay();
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push(dateStr);
    }
    return days;
  };

  // Check if a date is in the past
  const isPastDate = (dateStr) => {
    const dateObj = new Date(dateStr + 'T12:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dateObj < today;
  };

  // Check if date is today
  const isToday = (dateStr) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return dateStr === todayStr;
  };

  // Toggle date selection for admin PTO
  const toggleAdminDate = (dateStr) => {
    if (isPastDate(dateStr)) return;
    if (adminSelectedDates.includes(dateStr)) {
      setAdminSelectedDates(adminSelectedDates.filter(d => d !== dateStr));
    } else {
      setAdminSelectedDates([...adminSelectedDates, dateStr].sort());
    }
  };

  // Submit admin PTO with auto-approval
  const handleAdminPTOSubmit = async () => {
    if (adminSelectedDates.length === 0) {
      setMessage('❌ Please select at least one date');
      return;
    }

    try {
      const partialDayInfo = adminIsPartialDay ? {
        isPartial: true,
        startTime: adminPartialStartTime,
        hours: adminPartialHours
      } : null;
      
      // Create the PTO request
      const newRequest = await dataStore.createPTORequest(currentUser.id, adminSelectedDates, adminReason, partialDayInfo);
      
      // Auto-approve it immediately
      await dataStore.approvePTORequest(newRequest.id, currentUser);
      
      // Reset form
      setAdminSelectedDates([]);
      setAdminReason('');
      setAdminIsPartialDay(false);
      setAdminPartialStartTime('08:00');
      setAdminPartialHours(4);
      setShowAdminPTOForm(false);
      
      loadData();
      setMessage('✅ Your PTO has been submitted and approved!');
    } catch (err) {
      console.error('Error submitting admin PTO:', err);
      setMessage('❌ Error: ' + err.message);
    }
  };

  // =============================================
  // STYLES
  // =============================================
  const containerStyle = {
    padding: '20px',
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: theme.cardBackground,
    padding: '25px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    marginBottom: '20px'
  };

  const titleStyle = {
    color: theme.text,
    marginTop: 0
  };

  const buttonStyle = {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    color: darkMode ? '#fff' : '#000',
    marginRight: '8px',
    fontSize: '0.85rem'
  };

  const filterBtnStyle = (isActive) => ({
    padding: '8px 16px',
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: isActive ? colors.blue : theme.background,
    color: isActive ? (darkMode ? '#fff' : '#000') : theme.text,
    marginRight: '8px'
  });

  const statusBadge = (status) => {
    let bgColor, textColor;
    switch (status) {
      case 'Approved':
        bgColor = colors.green;
        textColor = darkMode ? '#1a1a2e' : '#fff';
        break;
      case 'Denied':
        bgColor = colors.red;
        textColor = '#fff';
        break;
      default: // Pending
        bgColor = colors.pink;
        textColor = '#333';
    }
    return {
      padding: '4px 12px',
      borderRadius: '12px',
      backgroundColor: bgColor,
      color: textColor,
      fontSize: '0.85rem',
      fontWeight: 'bold',
      display: 'inline-block'
    };
  };

  const requestCardStyle = {
    backgroundColor: theme.backgroundSecondary,
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '15px',
    border: `1px solid ${theme.border}`
  };

  const messageStyle = {
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '15px',
    textAlign: 'center',
    backgroundColor: colors.green,
    color: darkMode ? '#1a1a2e' : '#fff',
    fontWeight: '600'
  };

  // Count by status
  const pendingCount = ptoRequests.filter(r => r.status === 'Pending').length;
  const approvedCount = ptoRequests.filter(r => r.status === 'Approved').length;
  const deniedCount = ptoRequests.filter(r => r.status === 'Denied').length;

  // =============================================
  // RENDER
  // =============================================
  return (
    <div style={containerStyle}>
      {/* Two-column layout */}
      <div style={{ display: 'flex', gap: '20px', alignItems: 'stretch' }}>
        {/* Team Approved PTO Card - Left Side */}
        <div style={{ ...cardStyle, flex: '0 0 300px', marginBottom: 0 }}>
          <h2 style={{ ...titleStyle, fontSize: '1.3rem', marginBottom: '15px' }}>👥 Team PTO</h2>
          
          {/* Month Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <button
              type="button"
              onClick={prevMonth}
              style={{
                padding: '6px 12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                backgroundColor: theme.background,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              ‹
            </button>
            <span style={{ color: theme.text, fontWeight: 'bold', fontSize: '0.95rem' }}>
              {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              style={{
                padding: '6px 12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                backgroundColor: theme.background,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              ›
            </button>
          </div>
          
          {/* Approved PTO List */}
          {(() => {
            const approvedGrouped = getApprovedPTOGrouped();
            
            if (approvedGrouped.length === 0) {
              return <p style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>No approved PTO this month.</p>;
            }
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '500px', overflowY: 'auto' }}>
                {approvedGrouped.map((entry, idx) => (
                  <div 
                    key={idx}
                    style={{
                      padding: '8px 10px',
                      backgroundColor: theme.backgroundSecondary,
                      borderRadius: '6px',
                      borderLeft: `3px solid ${colors.green}`,
                      fontSize: '0.85rem',
                      color: theme.text,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ fontWeight: '600' }}>
                      {entry.startDate === entry.endDate 
                        ? formatDateShort(entry.startDate)
                        : `${formatDateShort(entry.startDate)} - ${formatDateShort(entry.endDate)}`
                      }
                    </span>
                    <span>{entry.teacherName}</span>
                    {entry.isPartialDay && (
                      <span style={{ 
                        fontSize: '0.75rem', 
                        color: theme.textSecondary,
                        backgroundColor: theme.hover,
                        padding: '2px 6px',
                        borderRadius: '3px'
                      }}>
                        {(() => {
                          const [h, m] = entry.partialStartTime.split(':').map(Number);
                          const amPm = h >= 12 ? 'PM' : 'AM';
                          const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                          return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
                        })()} ({entry.partialHours}h)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
          
          {/* Admin PTO Submission Section */}
          <div style={{ marginTop: '20px', borderTop: `1px solid ${theme.border}`, paddingTop: '15px' }}>
            <button
              onClick={() => setShowAdminPTOForm(!showAdminPTOForm)}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: colors.blue,
                color: darkMode ? '#fff' : '#000',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}
            >
              {showAdminPTOForm ? '✕ Cancel' : '➕ Request My PTO'}
            </button>
            
            {showAdminPTOForm && (
              <div style={{ marginTop: '15px' }}>
                {/* Mini Calendar */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px',
                    marginBottom: '6px'
                  }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                      <div key={i} style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        color: theme.textSecondary,
                        padding: '2px'
                      }}>
                        {day}
                      </div>
                    ))}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px'
                  }}>
                    {getCalendarDays().map((dateStr, index) => {
                      if (!dateStr) {
                        return <div key={`empty-${index}`} style={{ padding: '4px' }} />;
                      }
                      const isSelected = adminSelectedDates.includes(dateStr);
                      const isPast = isPastDate(dateStr);
                      const isTodayDate = isToday(dateStr);
                      const dayNum = parseInt(dateStr.split('-')[2]);
                      
                      return (
                        <button
                          key={dateStr}
                          type="button"
                          onClick={() => toggleAdminDate(dateStr)}
                          disabled={isPast}
                          style={{
                            padding: '4px',
                            border: isTodayDate ? `2px solid ${colors.blue}` : 'none',
                            borderRadius: '3px',
                            backgroundColor: isSelected ? colors.blue : (isPast ? 'transparent' : theme.backgroundSecondary),
                            color: isSelected ? '#fff' : (isPast ? theme.textSecondary : theme.text),
                            cursor: isPast ? 'not-allowed' : 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: isSelected || isTodayDate ? 'bold' : 'normal',
                            opacity: isPast ? 0.4 : 1
                          }}
                        >
                          {dayNum}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Selected Dates Display */}
                {adminSelectedDates.length > 0 && (
                  <div style={{ marginBottom: '10px', fontSize: '0.8rem', color: theme.text }}>
                    <strong>Selected:</strong> {adminSelectedDates.map(d => formatDateShort(d)).join(', ')}
                  </div>
                )}
                
                {/* Reason Input */}
                <input
                  type="text"
                  value={adminReason}
                  onChange={(e) => setAdminReason(e.target.value)}
                  placeholder="Reason (optional)"
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginBottom: '10px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '4px',
                    backgroundColor: theme.background,
                    color: theme.text,
                    fontSize: '0.85rem',
                    boxSizing: 'border-box'
                  }}
                />
                
                {/* Partial Day Toggle */}
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '10px',
                  fontSize: '0.85rem',
                  color: theme.text,
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={adminIsPartialDay}
                    onChange={(e) => setAdminIsPartialDay(e.target.checked)}
                  />
                  Partial Day
                </label>
                
                {adminIsPartialDay && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <select
                      value={adminPartialStartTime}
                      onChange={(e) => setAdminPartialStartTime(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '4px',
                        backgroundColor: theme.background,
                        color: theme.text,
                        fontSize: '0.8rem'
                      }}
                    >
                      {['07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00'].map(t => (
                        <option key={t} value={t}>{parseInt(t) > 12 ? `${parseInt(t)-12}:00 PM` : `${parseInt(t)}:00 ${parseInt(t) === 12 ? 'PM' : 'AM'}`}</option>
                      ))}
                    </select>
                    <select
                      value={adminPartialHours}
                      onChange={(e) => setAdminPartialHours(parseInt(e.target.value))}
                      style={{
                        flex: 1,
                        padding: '6px',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '4px',
                        backgroundColor: theme.background,
                        color: theme.text,
                        fontSize: '0.8rem'
                      }}
                    >
                      {[1,2,3,4,5,6,7,8].map(h => (
                        <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Submit Button */}
                <button
                  onClick={handleAdminPTOSubmit}
                  disabled={adminSelectedDates.length === 0}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: adminSelectedDates.length === 0 ? theme.textSecondary : colors.green,
                    color: darkMode ? '#1a1a2e' : '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: adminSelectedDates.length === 0 ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  Submit & Approve
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main PTO Management Card - Right Side */}
        <div style={{ ...cardStyle, flex: '1', marginBottom: 0 }}>
        <h1 style={titleStyle}>🏖️ PTO Request Management</h1>
        
        {/* Summary Stats */}
        <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
          <div style={{ padding: '10px 20px', backgroundColor: colors.pink, borderRadius: '8px' }}>
            <strong style={{ color: '#333' }}>{pendingCount}</strong>
            <span style={{ color: '#333', marginLeft: '5px' }}>Pending</span>
          </div>
          <div style={{ padding: '10px 20px', backgroundColor: colors.green, borderRadius: '8px' }}>
            <strong style={{ color: darkMode ? '#1a1a2e' : '#fff' }}>{approvedCount}</strong>
            <span style={{ color: darkMode ? '#1a1a2e' : '#fff', marginLeft: '5px' }}>Approved</span>
          </div>
          <div style={{ padding: '10px 20px', backgroundColor: colors.red, borderRadius: '8px' }}>
            <strong style={{ color: darkMode ? '#fff' : '#000' }}>{deniedCount}</strong>
            <span style={{ color: darkMode ? '#fff' : '#000', marginLeft: '5px' }}>Denied</span>
          </div>
        </div>

        {/* Message */}
        {message && <div style={messageStyle}>{message}</div>}

        {/* Filter Buttons */}
        <div style={{ marginBottom: '20px' }}>
          <span style={{ color: theme.textSecondary, marginRight: '10px' }}>Filter:</span>
          {['All', 'Pending', 'Approved', 'Denied'].map(status => (
            <button
              key={status}
              style={filterBtnStyle(filter === status)}
              onClick={() => setFilter(status)}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Request List */}
        {sortedRequests.length === 0 ? (
          <p style={{ textAlign: 'center', color: theme.textSecondary, padding: '40px' }}>
            {filter === 'All' 
              ? 'No PTO requests yet.' 
              : `No ${filter.toLowerCase()} PTO requests.`}
          </p>
        ) : (
          sortedRequests.map(request => (
            <div key={request.id} style={requestCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ color: theme.text, margin: '0 0 10px 0' }}>
                    👤 {getUserName(request.userId)}
                  </h3>
                  <div style={{ marginBottom: '10px' }}>
                    <span style={statusBadge(request.status)}>{request.status}</span>
                  </div>
                  <div style={{ color: theme.textSecondary, fontSize: '0.9rem', marginBottom: '10px' }}>
                    <strong>Dates:</strong>{' '}
                    {formatDateRanges(request.dates)}
                    {request.isPartialDay && (
                      <span style={{ marginLeft: '10px', color: colors.blue }}>
                        ⏰ {(() => {
                          const [h, m] = request.partialStartTime.split(':').map(Number);
                          const amPm = h >= 12 ? 'PM' : 'AM';
                          const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                          return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
                        })()} ({request.partialHours} hours)
                      </span>
                    )}
                  </div>
                  {request.reason && (
                    <div style={{ color: theme.textSecondary, fontSize: '0.9rem', marginBottom: '10px' }}>
                      <strong>Reason:</strong> {request.reason}
                    </div>
                  )}
                  <div style={{ color: theme.textSecondary, fontSize: '0.8rem' }}>
                    Submitted: {new Date(request.createdAt).toLocaleString()}
                  </div>
                </div>

                {/* Action Buttons - Only show for Pending */}
                {request.status === 'Pending' && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      style={{ ...buttonStyle, backgroundColor: colors.green, color: darkMode ? '#1a1a2e' : '#fff', fontWeight: '600' }}
                      onClick={() => handleApprove(request.id)}
                    >
                      ✓ Approve
                    </button>
                    <button
                      style={{ ...buttonStyle, backgroundColor: colors.red }}
                      onClick={() => handleDeny(request.id)}
                    >
                      ✗ Deny
                    </button>
                  </div>
                )}

                {/* Delete Button - Show for Approved requests */}
                {request.status === 'Approved' && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      style={{ ...buttonStyle, backgroundColor: colors.red, fontWeight: '600' }}
                      onClick={() => handleDelete(request.id)}
                      title="Delete this approved PTO"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      </div>
    </div>
  );
}

export default PTOManagement;
